const lineHeight = {
  display: {
    // Unitless on purpose: the font-size is `8vw`, so a rem line-height
    // wouldn't scale with it. A ratio keeps it proportional at every viewport.
    large: "1.071",
  },
  headline: {
    largerSans: "2.625rem",
    mediumSans: "2.25rem",
    mediumSerif: "2.625rem",
  },
  heading: {
    one: "2.875rem",
    two: "2.625rem",
    three: "2.25rem",
    four: "1.5625rem",
  },
  title: {
    mediumSans: "1.5625rem",
  },
  body: {
    largeSans: "1.75rem",
    default: "1.625rem",
    medium: "1.625rem",
    mediumSans: "1.625rem",
  },
  meta: {
    large: "1.278rem",
    medium: "1.375rem",
    small: "0.875rem",
    xsmall: "0.72rem",
  },
  tagline: "1.5rem",
};
//1rem = 16px
//0.25rem = 4px
const font = {
  display: {
    large: `normal 700 8vw /${lineHeight.display.large} Roobert, sans-serif`,
  },
  headline: {
    largerSans: `normal 700 2.25rem /${lineHeight.headline.largerSans} Roobert, sans-serif`,
    mediumSans: `normal 500 1.75rem /${lineHeight.headline.mediumSans} Roobert, sans-serif`,
    mediumSerif: `normal 600 1.75rem /${lineHeight.headline.mediumSerif} nib, serif`,
  },
  heading: {
    one: `normal 700 2.75rem /${lineHeight.heading.one} Roobert, sans-serif`,
    two: `normal 700 2.25rem /${lineHeight.heading.two} Roobert, sans-serif`,
    three: `normal 500 1.75rem /${lineHeight.heading.three} Roobert, sans-serif`,
    four: `normal 700 1.25rem /${lineHeight.heading.four} Roobert, sans-serif`,
  },
  title: {
    mediumSans: `normal 700 1.25rem /${lineHeight.title.mediumSans} Roobert, sans-serif`,
  },
  body: {
    largeSans: `normal 500 1.25rem /${lineHeight.body.largeSans} Roobert, sans-serif`,
    default: `normal 400 1.125rem /${lineHeight.body.default} Roobert, sans-serif`,
    medium: `normal 400 1.125rem /${lineHeight.body.medium} Roobert, sans-serif`,
    mediumSans: `normal 400 1.125rem /${lineHeight.body.mediumSans} Roobert, sans-serif`,
  },
  meta: {
    large: `normal 400 1.125rem /${lineHeight.meta.large} Roobert, sans-serif`,
    medium: `normal 400 0.875rem /${lineHeight.meta.medium} Roobert, sans-serif`,
    small: `normal 700 0.75rem /${lineHeight.meta.small} Roobert, sans-serif`,
    xsmall: `normal 400 0.875rem /${lineHeight.meta.xsmall} Roobert, sans-serif`,
  },
  tagline: `normal 400 1rem /${lineHeight.tagline} Roobert, sans-serif`,

  experimental: {
    headline: {
      nib: `normal 500 2.25rem /${lineHeight.headline.largerSans} nib, serif`,
    },
  },
};

const rootColors = {
  neutral100: "#FFFFFF",
  neutral90: "#ECECEC",
  neutral80: "#CCCCCC",
  neutral60: "#999999",
  neutral40: "#666666",
  neutral20: "#333333",
  neutral10: "#121212",
  neutral0: "#000000",
  primary: "#A951ED",
  secondary90: "#AEA0DA",
  secondary80: "#7C6AAC", //purple500
  secondary60: "#9021A6",
  secondary70: "#B224B2",
  tertiary60: "#FF9966",
};

const color = {
  light: {
    action: {
      disabled: rootColors.neutral40, // Deprecated - Use text.disabled instead
      input: rootColors.neutral10,
      border: rootColors.neutral60,
    },
    text: {
      primary: rootColors.neutral0,
      secondary: rootColors.neutral20,
      interactive: rootColors.secondary60,
      onSurfacePrimary: rootColors.neutral100,
      onSurfaceHighlightSecondary: rootColors.neutral100,
      hover: rootColors.secondary70,
      disabled: rootColors.neutral40,
      inactive: rootColors.neutral80,
    },
    surface: {
      background: rootColors.neutral100,
      primary: rootColors.neutral90,
      highlight: rootColors.secondary80,
      highlightSecondary: rootColors.secondary60,
      neutral: rootColors.secondary90,
      disabled: rootColors.neutral90,
    },
  },
  dark: {
    text: {
      primary: rootColors.neutral100,
      secondary: rootColors.neutral80,
      interactive: rootColors.tertiary60,
    },
    surface: {
      background: rootColors.neutral10,
      primary: rootColors.neutral20,
      secondary: rootColors.neutral40,
      highlight: rootColors.secondary80,
    },
  },
  core: {
    primary: rootColors.primary,
    pink500: "#7A17B6",
    pink200: "#BA55D3",
    deepViolet: "#5E17B6",
    purple300: "#AEA0DA",
    purple400: "#9585C7",
    purple650: "#833C94",
    purple700: "#403B4F",
  },
  neutral: {
    black: "#000000",
    white: "#FFFFFF",
  },
  functional: {
    pink400: "#BD00FF",
    error: "#D60505",
    errorAlpha: "#FF9592",
    errorBackground: "#461D24",
    separator: rootColors.neutral80,
  },
  full: {
    gray100: "#F9F9F9",
    gray200: "#EEEEEE",
    gray300: "#E3E3E3",
    gray400: "#C9C9C9",
    gray500: "#938E8F",
    gray600: "#6E6B6C",
    gray650: "#606060",
    gray700: "#4C4B4B",
    gray750: "#464646",
    gray800: "#2A2929",
    purple100: "#EAE4FB",
    purple200: "#CBBFED",
    purple500: "#7C6AAC",
    pink300: "#DE58FF",
    red: "#FC1266",
    backdrop: "rgba(110, 107, 108, 0.9)",
    green: "#47BE85",
  },
};

const radius = {
  minuscule: "3px",
  tiny: "6px",
  small: "12px",
  medium: "24px",
  22: "22px",
  32: "32px",
  48: "48px",
};

const space = {
  4: "4px",
  8: "8px",
  10: "10px",
  12: "12px",
  16: "16px",
  20: "20px",
  22: "22px",
  24: "24px",
  28: "28px",
  32: "32px",
  36: "36px",
  40: "40px",
  56: "56px",
  64: "64px",
  80: "80px",
  96: "96px",
};

const width = {
  sidebar: "180px",
  20: "20px",
};

const height = {
  64: "64px",
};

//sm is the xs breakpoint of readix
const mediaQuery = {
  sm: "screen and (max-width: 520px)", // If you change this, also change the value in globals.css
  lgDeprecated: "screen and (max-width: 800px)",
  md: "screen and (min-width: 521px) and (max-width: 1023px)",
  lg: "screen and (min-width: 1024px)",
};

const tokens = {
  font,
  color,
  radius,
  space,
  width,
  height,
  lineHeight,
  mediaQuery,
};

export default tokens;
